import React, { useState, useEffect, useRef, ReactElement, useCallback } from 'react'
import _ from 'lodash'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

import { ChannelsConfigurationBtn } from '../styles/ActionButtonStyle'
import { fetchRigFloorReprocessingStatus, startRigFloorReprocess, stopRigFloorReprocessing } from '../../services/api'

import { Divisor, FixedWidthConfigSection, MainContainer, Section, SectionRow } from '../styles/common'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezonePlugin from 'dayjs/plugin/timezone'

import i18n from 'live/services/i18n'
import theme from 'live/services/theme'
import globalNotification from 'live/services/global-notification'

import { SelectOptions } from '../../types'

import {
    ChannelsConfigurationModal,
    ChannelsInterpolationBadge,
    EquipmentSelectSection,
    PERFORMANCE_AND_RIG_FLOOR_KPIS_METHODOLOGY,
    PERFORMANCE_KPIS_METHODOLOGY
} from '../common'
import { KPISDrillingRegistryContextType } from '../../types/kpis-drilling/kpis-drilling-registry-type'
import { useEquipments } from '../../hooks/useEquipments'
import { usePopulateRigFloorConfig } from '../../hooks/usePopulateRigFloorConfig'
import { useRigFloorMethodologyLogic } from '../../hooks/useRigFloorMethodologyLogic'
import { saveRigFloorProcessConfig, secondsToMilliseconds } from '../../utils/functions'
import { useDisabledWhenNotSelectedMethodoly } from '../../hooks/useDisabledWhenNotSelectedMethodoly'
import { useRigFloorReprocessingPolling } from '../../hooks/useRigFloorReprocessingPolling'

dayjs.extend(utc)
dayjs.extend(timezonePlugin)

// "Rig Floor Equipments"/"Riser Equipments" group sub-header — Source Sans Pro Regular 16px/20px
const groupTitleStyle = { fontSize: '16px', lineHeight: '20px', fontWeight: 400 }

export const ReprocessRigFloorKpis = (props: KPISDrillingRegistryContextType): ReactElement => {
    const { savedKpiOptions, reprocessingSpan, hasPermissionToEdit, setReprocessingSpan } =
        props?.contextState?.reprocess?.props || {}
    const { rigId, eventType, setAdditionalOutputEventName, setAdditionalOutputEventValue } =
        props?.contextState?.service || {}
    const { selectedMethodology, setSelectedMethodology } = props?.contextState?.reprocess?.rigfloor || {}
    const {
        setShowStopReprocessModal,
        setIsStopDisabled,
        selectedMethod,
        setIsSomeReprocessActivated,
        isSomeReprocessActivated
    } = props?.contextState?.reprocess?.state || {}

    const { setShowReprocessNotifyMessage } = props?.contextState?.reprocess?.props || {}

    const prevProps = useRef(null)

    const [selectedHydratongEquipment, setSelectedHydratongEquipment] = useState<SelectOptions>()
    const [selectedHydrarackerEquipment, setSelectedHydrarackerEquipment] = useState<SelectOptions>()
    const [selectedTopDriveEquipment, setSelectedTopDriveEquipment] = useState<SelectOptions>()
    const [selectedRrtEquipment, setSelectedRrtEquipment] = useState<SelectOptions>()
    const [selectedCatwalkEquipment, setSelectedCatwalkEquipment] = useState<SelectOptions>()

    const [interpolationFrequency, setInterpolationFrequency] = useState<number>()
    const [maxDeltaWithoutInterpolation, setMaxDeltaWithoutInterpolation] = useState<number>()

    // Riser channels config — independent from Rig Floor's (RS-12). Not sent to the reprocess-start
    // endpoint (RRT/Catwalk have no state-generation service yet), only persisted in the config DTO.
    const [isUsingRiserInterpolation, setIsUsingRiserInterpolation] = useState<boolean>()
    const [riserInterpolationFrequency, setRiserInterpolationFrequency] = useState<number>()
    const [riserMaxDeltaWithoutInterpolation, setRiserMaxDeltaWithoutInterpolation] = useState<number>()
    const [isRiserChannelsOpened, setIsRiserChannelsOpened] = useState<boolean>(false)

    const [canExecuteRigFloor, setCanExecuteRigFloor] = useState<boolean>()
    const [isReprocessingData, setIsReprocessingData] = useState<boolean>(false)
    const [isRigFloorReprocessing, setIsRigFloorReprocessing] = useState(false)
    const [isRigFloorWaiting, setIsRigFloorWaiting] = useState(false)
    const [isRigFloorChannelsOpened, setIsRigFloorChannelsOpened] = useState<boolean>(false)
    const [isUsingRigFloorInterpolation, setIsUsingRigFloorInterpolation] = useState<boolean>()
    const [isWaitingTimeout, setIsWaitingTimeout] = useState(false)
    const [wasStopped, setWasStopped] = useState(false)

    const [enableRigFloor, setEnableRigFloor] = useState<boolean>(false)
    const prevKpiStatus = useRef<boolean>(savedKpiOptions?.isReprocessRunning)

    const waitingTimeoutRef = useRef<number | null>(null)

    const {
        hydratongEquipments,
        hydrarackerEquipments,
        topDriveEquipments,
        rrtEquipments,
        catwalkEquipments
    } = useEquipments({
        rigId,
        eventType,
        setAdditionalOutputEventName,
        setAdditionalOutputEventValue
    })

    // used to know if the rigfloor is registered in context state and avoid infinite loop
    const registeredInContext = useRef<{
        context: boolean
        hydratong: boolean
        hydraracker: boolean
        topdrive: boolean
    }>({
        context: false,
        hydratong: false,
        hydraracker: false,
        topdrive: false
    })

    const LOCAL_CAN_EXECUTE_RIG_FLOOR = 'canExecuteRigFloor'
    const LOCAL_STORAGE_SELECTED_METHOD = 'selectedMethod'
    const LOCAL_STORAGE_TIMEOUT_KEY = 'isWaitingTimeout'
    const LOCAL_STORAGE_SPAN_KEY = 'reprocessingSpan'
    const LOCAL_STORAGE_RIG_ID_KEY = 'rigId'
    const LOCAL_STORAGE_INTERPOLATION_KEY = 'isUsingRigFloorInterpolation'
    const LOCAL_STORAGE_INTERPOLATION_FREQ_KEY = 'interpolationFrequency'
    const LOCAL_STORAGE_MAX_DELTA_KEY = 'maxDeltaWithoutInterpolation'
    const LOCAL_STORAGE_RISER_INTERPOLATION_KEY = 'isUsingRiserInterpolation'
    const LOCAL_STORAGE_RISER_INTERPOLATION_FREQ_KEY = 'riserInterpolationFrequency'
    const LOCAL_STORAGE_RISER_MAX_DELTA_KEY = 'riserMaxDeltaWithoutInterpolation'
    const LOCAL_STORAGE_HYDRATONG_KEY = 'hydratong'
    const LOCAL_STORAGE_HYDRARACKER_KEY = 'hydraracker'
    const LOCAL_STORAGE_TOP_DRIVE_KEY = 'topDrive'
    const LOCAL_STORAGE_RRT_KEY = 'rrt'
    const LOCAL_STORAGE_CATWALK_KEY = 'catwalk'

    const TIMEOUT_DURATION = 10000

    useRigFloorMethodologyLogic({
        selectedMethodology,
        setEnableRigFloor,
        setIsActButtonDisabled: setIsStopDisabled,
        selectedHydratongEquipment,
        selectedHydrarackerEquipment,
        selectedTopDriveEquipment,
        reprocessingSpan
    })

    usePopulateRigFloorConfig({
        setSelectedHydratongEquipment,
        setSelectedHydrarackerEquipment,
        setSelectedTopDriveEquipment,
        setSelectedRrtEquipment,
        setSelectedCatwalkEquipment,
        setSelectedMethodology,
        setIsUsingRigFloorInterpolation,
        setInterpolationFrequency,
        setMaxDeltaWithoutInterpolation,
        setIsUsingRiserInterpolation,
        setRiserInterpolationFrequency,
        setRiserMaxDeltaWithoutInterpolation,
        assetId: rigId,
        assetType: 'rig',
        operationType: 'reprocessing',
        hydratongEquipments,
        hydrarackerEquipments,
        topDriveEquipments,
        rrtEquipments,
        catwalkEquipments,
        setEnableRigFloor,
        setContextState: props?.setContextState
    })

    useDisabledWhenNotSelectedMethodoly({
        selectedMethodology,
        setEnableRigFloor,
        setIsActButtonDisabled: setIsStopDisabled
    })

    useRigFloorReprocessingPolling({
        rigId,
        isRigFloorReprocessing,
        setIsRigFloorReprocessing,
        setIsSomeReprocessActivated,
        setContextState: props?.setContextState
    })

    const cleanStorageData = (): void => {
        localStorage.setItem(LOCAL_STORAGE_TIMEOUT_KEY, '0')
        localStorage.removeItem(LOCAL_STORAGE_SPAN_KEY)
        localStorage.removeItem(LOCAL_STORAGE_RIG_ID_KEY)
        localStorage.removeItem(LOCAL_STORAGE_INTERPOLATION_KEY)
        localStorage.removeItem(LOCAL_CAN_EXECUTE_RIG_FLOOR)
        localStorage.removeItem(LOCAL_STORAGE_INTERPOLATION_FREQ_KEY)
        localStorage.removeItem(LOCAL_STORAGE_MAX_DELTA_KEY)
        localStorage.removeItem(LOCAL_STORAGE_RISER_INTERPOLATION_KEY)
        localStorage.removeItem(LOCAL_STORAGE_RISER_INTERPOLATION_FREQ_KEY)
        localStorage.removeItem(LOCAL_STORAGE_RISER_MAX_DELTA_KEY)
        localStorage.removeItem(LOCAL_STORAGE_HYDRATONG_KEY)
        localStorage.removeItem(LOCAL_STORAGE_HYDRARACKER_KEY)
        localStorage.removeItem(LOCAL_STORAGE_TOP_DRIVE_KEY)
        localStorage.removeItem(LOCAL_STORAGE_RRT_KEY)
        localStorage.removeItem(LOCAL_STORAGE_CATWALK_KEY)
        localStorage.removeItem(LOCAL_STORAGE_SELECTED_METHOD)
    }

    const executeRigFloorReprocessingQuery = useCallback(
        async (
            span: string,
            rigId: string,
            isUsingRigFloorInterpolation: boolean,
            interpolationFrequency: number,
            maxDeltaWithoutInterpolation: number,
            hydratong?: string,
            hydraracker?: string,
            topDrive?: string,
            rrt?: string,
            catwalk?: string
        ): Promise<void> => {
            if (wasStopped) return

            const { startTimestamp, endTimestamp } = getTimestamps(span)
            const diffInDays = getDifferenceInDays(startTimestamp, endTimestamp)

            if (diffInDays > 60) return

            if (rigId !== undefined) {
                const res = await startRigFloorReprocess(
                    rigId,
                    isUsingRigFloorInterpolation,
                    secondsToMilliseconds(interpolationFrequency ?? 0),
                    secondsToMilliseconds(maxDeltaWithoutInterpolation ?? 0),
                    startTimestamp,
                    endTimestamp,
                    hydratong,
                    hydraracker,
                    topDrive,
                    rrt,
                    catwalk
                )

                if (res.ok) {
                    setIsRigFloorReprocessing(true)
                    props?.setContextState(prevState => ({
                        ...prevState,
                        reprocess: {
                            ...prevState?.reprocess,
                            rigfloor: {
                                ...prevState?.reprocess?.rigfloor,
                                isRigFloorWaiting: false,
                                isRigFloorReprocessing: true
                            }
                        }
                    }))
                }
            } else {
                globalNotification.error('Unable to start the Rig Floor reprocess, invalid asset name.')
            }
        },
        [props, wasStopped]
    )

    const startRigFloorReprocessWithTimeout = useCallback((): void => {
        const previousCanExecuteRigFloor = localStorage.getItem(LOCAL_CAN_EXECUTE_RIG_FLOOR)
        const previousSelectedMethodology = localStorage.getItem(LOCAL_STORAGE_SELECTED_METHOD)
        const previousSpan = localStorage.getItem(LOCAL_STORAGE_SPAN_KEY)
        const previousRigId = localStorage.getItem(LOCAL_STORAGE_RIG_ID_KEY)
        const previousInterpolation = localStorage.getItem(LOCAL_STORAGE_INTERPOLATION_KEY)
        const previousInterpolationFrequency = localStorage.getItem(LOCAL_STORAGE_INTERPOLATION_FREQ_KEY)
        const previousMaxDelta = localStorage.getItem(LOCAL_STORAGE_MAX_DELTA_KEY)
        const previousRiserInterpolation = localStorage.getItem(LOCAL_STORAGE_RISER_INTERPOLATION_KEY)
        const previousRiserInterpolationFrequency = localStorage.getItem(LOCAL_STORAGE_RISER_INTERPOLATION_FREQ_KEY)
        const previousRiserMaxDelta = localStorage.getItem(LOCAL_STORAGE_RISER_MAX_DELTA_KEY)
        const previousHydratong = localStorage.getItem(LOCAL_STORAGE_HYDRATONG_KEY)
        const previousHydraracker = localStorage.getItem(LOCAL_STORAGE_HYDRARACKER_KEY)
        const previousTopDrive = localStorage.getItem(LOCAL_STORAGE_TOP_DRIVE_KEY)
        const previousRrt = localStorage.getItem(LOCAL_STORAGE_RRT_KEY)
        const previousCatwalk = localStorage.getItem(LOCAL_STORAGE_CATWALK_KEY)

        const spanToUse = reprocessingSpan || previousSpan
        const rigIdToUse = rigId ?? (previousRigId ? String(previousRigId) : undefined)
        const selectedMethodologyToUse = selectedMethodology?.value || previousSelectedMethodology
        const isBothSelected = selectedMethodologyToUse === PERFORMANCE_AND_RIG_FLOOR_KPIS_METHODOLOGY
        const canExecuteRigFloorToUse =
            canExecuteRigFloor ?? (previousCanExecuteRigFloor ? previousCanExecuteRigFloor === 'true' : undefined)
        const interpolationToUse =
            isUsingRigFloorInterpolation ?? (previousInterpolation ? previousInterpolation === 'true' : undefined)
        const interpolationFreqToUse =
            interpolationFrequency ??
            (previousInterpolationFrequency ? Number(previousInterpolationFrequency) : undefined)
        const maxDeltaToUse = maxDeltaWithoutInterpolation ?? (previousMaxDelta ? Number(previousMaxDelta) : undefined)
        const riserInterpolationToUse =
            isUsingRiserInterpolation ?? (previousRiserInterpolation ? previousRiserInterpolation === 'true' : undefined)
        const riserInterpolationFreqToUse =
            riserInterpolationFrequency ??
            (previousRiserInterpolationFrequency ? Number(previousRiserInterpolationFrequency) : undefined)
        const riserMaxDeltaToUse =
            riserMaxDeltaWithoutInterpolation ?? (previousRiserMaxDelta ? Number(previousRiserMaxDelta) : undefined)
        const hydratongToUse = selectedHydratongEquipment?.value || previousHydratong
        const hydrarackerToUse = selectedHydrarackerEquipment?.value || previousHydraracker
        const topDriveToUse = selectedTopDriveEquipment?.value || previousTopDrive
        const rrtToUse = selectedRrtEquipment?.value || previousRrt
        const catwalkToUse = selectedCatwalkEquipment?.value || previousCatwalk

        if (!spanToUse || !rigIdToUse || !isBothSelected || wasStopped) {
            cleanStorageData()
            setWasStopped(false)

            return
        }

        if (!reprocessingSpan && previousSpan) setReprocessingSpan(previousSpan)

        localStorage.setItem(LOCAL_STORAGE_TIMEOUT_KEY, '1')
        localStorage.setItem(LOCAL_STORAGE_SPAN_KEY, spanToUse)
        localStorage.setItem(LOCAL_STORAGE_RIG_ID_KEY, String(rigIdToUse))
        localStorage.setItem(LOCAL_CAN_EXECUTE_RIG_FLOOR, String(canExecuteRigFloorToUse ?? false))
        localStorage.setItem(LOCAL_STORAGE_INTERPOLATION_KEY, String(interpolationToUse ?? false))
        localStorage.setItem(LOCAL_STORAGE_INTERPOLATION_FREQ_KEY, String(interpolationFreqToUse ?? 0))
        localStorage.setItem(LOCAL_STORAGE_MAX_DELTA_KEY, String(maxDeltaToUse ?? 0))
        localStorage.setItem(LOCAL_STORAGE_RISER_INTERPOLATION_KEY, String(riserInterpolationToUse ?? false))
        localStorage.setItem(LOCAL_STORAGE_RISER_INTERPOLATION_FREQ_KEY, String(riserInterpolationFreqToUse ?? 0))
        localStorage.setItem(LOCAL_STORAGE_RISER_MAX_DELTA_KEY, String(riserMaxDeltaToUse ?? 0))
        localStorage.setItem(LOCAL_STORAGE_HYDRATONG_KEY, hydratongToUse ?? '')
        localStorage.setItem(LOCAL_STORAGE_HYDRARACKER_KEY, hydrarackerToUse ?? '')
        localStorage.setItem(LOCAL_STORAGE_TOP_DRIVE_KEY, topDriveToUse ?? '')
        localStorage.setItem(LOCAL_STORAGE_RRT_KEY, rrtToUse ?? '')
        localStorage.setItem(LOCAL_STORAGE_CATWALK_KEY, catwalkToUse ?? '')
        localStorage.setItem(LOCAL_STORAGE_SELECTED_METHOD, selectedMethodologyToUse ?? '')

        setIsWaitingTimeout(true)
        if (typeof setIsSomeReprocessActivated === 'function') setIsSomeReprocessActivated(true)

        props?.setContextState(prevState => ({
            ...prevState,
            reprocess: {
                ...prevState?.reprocess,
                rigfloor: {
                    ...prevState?.reprocess?.rigfloor,
                    isRigFloorWaiting: true
                }
            }
        }))

        if (waitingTimeoutRef.current) {
            clearTimeout(waitingTimeoutRef.current)
            waitingTimeoutRef.current = null
        }

        waitingTimeoutRef.current = window.setTimeout(() => {
            if (wasStopped) {
                cleanStorageData()
                setIsWaitingTimeout(false)
                waitingTimeoutRef.current = null

                return
            }

            executeRigFloorReprocessingQuery(
                spanToUse,
                rigIdToUse,
                interpolationToUse ?? false,
                interpolationFreqToUse ?? 0,
                maxDeltaToUse ?? 0,
                hydratongToUse,
                hydrarackerToUse,
                topDriveToUse,
                rrtToUse,
                catwalkToUse
            )

            cleanStorageData()
            setIsWaitingTimeout(false)
            waitingTimeoutRef.current = null
        }, TIMEOUT_DURATION)
    }, [
        canExecuteRigFloor,
        executeRigFloorReprocessingQuery,
        interpolationFrequency,
        isUsingRigFloorInterpolation,
        maxDeltaWithoutInterpolation,
        isUsingRiserInterpolation,
        riserInterpolationFrequency,
        riserMaxDeltaWithoutInterpolation,
        props,
        reprocessingSpan,
        rigId,
        selectedHydrarackerEquipment?.value,
        selectedHydratongEquipment?.value,
        selectedMethodology?.value,
        selectedTopDriveEquipment?.value,
        selectedRrtEquipment?.value,
        selectedCatwalkEquipment?.value,
        setIsSomeReprocessActivated,
        setReprocessingSpan,
        wasStopped
    ])

    const getTimestamps = (
        rangeStr: string
    ): {
        startTimestamp: number
        endTimestamp: number
    } => {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        const [startStr, endStr] = rangeStr.split(' to ')

        const start = dayjs.tz(`${startStr}:00`, userTimezone)
        const end = dayjs.tz(`${endStr}:00`, userTimezone)

        return {
            startTimestamp: start.valueOf(),
            endTimestamp: end.valueOf()
        }
    }

    const getDifferenceInDays = (start: number, end: number): number => {
        return Math.abs(end - start) / (1000 * 60 * 60 * 24)
    }

    const rigFloorConfigRef = useRef<{
        interpolationFrequency: number | undefined
        maxDeltaWithoutInterpolation: number | undefined
        isUsingRigFloorInterpolation: boolean | undefined
        riserInterpolationFrequency: number | undefined
        riserMaxDeltaWithoutInterpolation: number | undefined
        isUsingRiserInterpolation: boolean | undefined
    }>({
        interpolationFrequency: undefined,
        maxDeltaWithoutInterpolation: undefined,
        isUsingRigFloorInterpolation: undefined,
        riserInterpolationFrequency: undefined,
        riserMaxDeltaWithoutInterpolation: undefined,
        isUsingRiserInterpolation: undefined
    })

    useEffect(() => {
        rigFloorConfigRef.current = {
            interpolationFrequency,
            maxDeltaWithoutInterpolation,
            isUsingRigFloorInterpolation,
            riserInterpolationFrequency,
            riserMaxDeltaWithoutInterpolation,
            isUsingRiserInterpolation
        }
    }, [
        interpolationFrequency,
        maxDeltaWithoutInterpolation,
        isUsingRigFloorInterpolation,
        riserInterpolationFrequency,
        riserMaxDeltaWithoutInterpolation,
        isUsingRiserInterpolation
    ])

    const resetRigFloorStates = useCallback(() => {
        setWasStopped(true)
        setIsRigFloorWaiting(false)
        setIsRigFloorReprocessing(false)
        setIsReprocessingData(false)
        setCanExecuteRigFloor(false)
        setIsWaitingTimeout(false)

        cleanStorageData()

        props?.setContextState(prev => ({
            ...prev,
            reprocess: {
                ...prev.reprocess,
                state: {
                    ...prev.reprocess.state,
                    isSomeReprocessActivated: false
                },
                rigfloor: {
                    ...prev.reprocess.rigfloor,
                    isRigFloorWaiting: false,
                    isReprocessingData: false,
                    isRigFloorReprocessing: false
                }
            }
        }))
    }, [props])

    const handleSaveRigFloorConfig = useCallback((): void => {
        const currentConfig = rigFloorConfigRef.current

        saveRigFloorProcessConfig({
            assetType: 'rig',
            assetId: rigId,
            operationType: 'reprocessing',
            hydratongEquipmentUUID: selectedHydratongEquipment?.value,
            hydrarackerEquipmentUUID: selectedHydrarackerEquipment?.value,
            topDriveEquipmentUUID: selectedTopDriveEquipment?.value,
            rrtEquipmentUUID: selectedRrtEquipment?.value,
            catwalkEquipmentUUID: selectedCatwalkEquipment?.value,
            rigFloorMethodology: selectedMethodology?.value,
            rigFloorUseInterpolation: currentConfig.isUsingRigFloorInterpolation ?? false,
            rigFloorInterpolationFrequency: currentConfig.interpolationFrequency ?? 0,
            rigFloorMaxDeltaWithoutInterpolation: currentConfig.maxDeltaWithoutInterpolation ?? 0,
            riserUseInterpolation: currentConfig.isUsingRiserInterpolation ?? false,
            riserInterpolationFrequency: currentConfig.riserInterpolationFrequency ?? 0,
            riserMaxDeltaWithoutInterpolation: currentConfig.riserMaxDeltaWithoutInterpolation ?? 0
        }).then(() => {
            props?.setContextState(prevState => ({
                ...prevState,
                reprocess: {
                    ...prevState?.reprocess,
                    rigfloor: {
                        ...prevState?.reprocess?.rigfloor,
                        // isRigFloorWaiting: true,
                        isRigFloorReprocessing: true
                    }
                }
            }))
        })
    }, [
        props,
        rigId,
        selectedHydrarackerEquipment?.value,
        selectedHydratongEquipment?.value,
        selectedMethodology?.value,
        selectedTopDriveEquipment?.value,
        selectedRrtEquipment?.value,
        selectedCatwalkEquipment?.value
    ])

    const handleStartProcess = useCallback(async (): Promise<void> => {
        if (selectedMethodology?.value === PERFORMANCE_KPIS_METHODOLOGY) {
            if (typeof setShowReprocessNotifyMessage === 'function') {
                setShowReprocessNotifyMessage(true)
            }
        }
        else if (selectedMethodology?.value === PERFORMANCE_AND_RIG_FLOOR_KPIS_METHODOLOGY) {
            if (typeof setShowReprocessNotifyMessage === 'function') {
                setShowReprocessNotifyMessage(false)
            }
            handleSaveRigFloorConfig()

            setWasStopped(false)
            setIsRigFloorWaiting(true)
            setCanExecuteRigFloor(true)
            if (typeof setIsSomeReprocessActivated === 'function') setIsSomeReprocessActivated(true)

            props?.setContextState(prevState => ({
                ...prevState,
                reprocess: {
                    ...prevState?.reprocess,
                    rigfloor: {
                        ...prevState?.reprocess?.rigfloor,
                        isRigFloorWaiting: true
                    }
                }
            }))
        }
    }, [handleSaveRigFloorConfig, props, selectedMethodology?.value, setIsSomeReprocessActivated])

    const handleStopProcess = useCallback(async () => {
        if (waitingTimeoutRef.current) {
            clearTimeout(waitingTimeoutRef.current)
            waitingTimeoutRef.current = null
        }

        if (rigId) {
            await stopRigFloorReprocessing(rigId)
        }

        resetRigFloorStates()
    }, [rigId, resetRigFloorStates])

    useEffect(() => {
        const isWaiting = localStorage.getItem(LOCAL_STORAGE_TIMEOUT_KEY) === '1'
        if (isWaiting) {
            startRigFloorReprocessWithTimeout()
        }

        return () => {
            if (waitingTimeoutRef.current) {
                clearTimeout(waitingTimeoutRef.current)
                waitingTimeoutRef.current = null
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const isPerformanceReprocessing = savedKpiOptions?.isReprocessRunning
        const isBothSelected = selectedMethodology?.value === PERFORMANCE_AND_RIG_FLOOR_KPIS_METHODOLOGY
        const isWaiting = (isPerformanceReprocessing && isBothSelected) || isWaitingTimeout

        if (!isBothSelected) return

        // Monitor reprocess completion and check for errors
        if (prevKpiStatus.current && !isPerformanceReprocessing && !isWaitingTimeout && canExecuteRigFloor) {
            const reprocessStatus = props?.contextState?.reprocess?.props?.reprocessStatus
            const hasExplicitError = reprocessStatus && reprocessStatus.toLowerCase().includes('error')

            const stoppedImmediately =
                !isPerformanceReprocessing &&
                prevKpiStatus.current &&
                reprocessStatus !== 'Initializing' &&
                !reprocessStatus

            if (hasExplicitError || stoppedImmediately) {
                resetRigFloorStates()
                setIsStopDisabled(false)
            } else if (reprocessStatus !== 'error' && reprocessStatus !== 'Initializing') {
                startRigFloorReprocessWithTimeout()
            }
        }

        prevKpiStatus.current = isPerformanceReprocessing
        setIsRigFloorWaiting(isWaiting)

        props?.setContextState(prevState => ({
            ...prevState,
            reprocess: {
                ...prevState?.reprocess,
                rigfloor: {
                    ...prevState?.reprocess?.rigfloor,
                    isRigFloorWaiting: isWaiting
                }
            }
        }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedKpiOptions, selectedMethodology, isRigFloorWaiting, isWaitingTimeout, canExecuteRigFloor])

    useEffect(() => {
        const currentReprocessingState = isReprocessingData
        const nextReprocessingState = isRigFloorReprocessing || savedKpiOptions?.isReprocessRunning

        if (!isRigFloorWaiting) {
            if (currentReprocessingState === true && nextReprocessingState === false) {
                setTimeout(() => {
                    globalNotification.success(i18n('Data reprocessing completed successfully'))
                    setReprocessingSpan(null)
                    setCanExecuteRigFloor(false)
                    cleanStorageData()
                    if (typeof setIsSomeReprocessActivated === 'function') setIsSomeReprocessActivated(false)
                }, 1000)
            }
        }

        if (selectedMethodology?.value === PERFORMANCE_AND_RIG_FLOOR_KPIS_METHODOLOGY) {
            props?.setContextState(prevState => ({
                ...prevState,
                reprocess: {
                    ...prevState?.reprocess,
                    rigfloor: {
                        ...prevState?.reprocess?.rigfloor,
                        isReprocessingData: nextReprocessingState,
                        isRigFloorReprocessing: nextReprocessingState
                    }
                }
            }))
            setIsReprocessingData(nextReprocessingState)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isReprocessingData,
        isRigFloorWaiting,
        reprocessingSpan,
        isRigFloorReprocessing,
        savedKpiOptions,
        selectedMethod,
        selectedMethodology
    ])

    useEffect(() => {
        fetchRigFloorReprocessingStatus(rigId).then(async res => {
            if (res.ok) {
                const data = await res.json()
                const isRunning = data?.status === 'running'
                setIsRigFloorReprocessing(isRunning)
                props?.setContextState(prevState => ({
                    ...prevState,
                    reprocess: {
                        ...prevState?.reprocess,
                        rigfloor: {
                            ...prevState?.reprocess?.rigfloor,
                            isRigFloorReprocessing: isRunning
                        }
                    }
                }))
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rigId])

    const updatePropsContextState = (): void => {
        if (typeof props?.setContextState === 'function' && !_.isEmpty(props?.contextState?.realtime?.state)) {
            props.setContextState(prevState => ({
                ...prevState,
                reprocess: {
                    ...prevState?.reprocess,
                    handleStartActionCallback: handleStartProcess,
                    handleStopActionCallback: handleStopProcess
                }
            }))

            prevProps.current = props
        }
    }

    // update context state when props change from kpis drilling
    useEffect(() => {
        if (prevProps != null && JSON.stringify(prevProps.current) === JSON.stringify(props)) {
            return
        }
        updatePropsContextState()
    }, [props])

    // update context state with handlers and rig floor functions
    useEffect(() => {
        updatePropsContextState()
    }, [
        rigId,
        interpolationFrequency,
        isUsingRigFloorInterpolation,
        maxDeltaWithoutInterpolation,
        selectedHydrarackerEquipment?.value,
        selectedHydratongEquipment?.value,
        selectedMethodology?.value,
        selectedTopDriveEquipment?.value,
        selectedRrtEquipment?.value,
        selectedCatwalkEquipment?.value
    ])

    useEffect(() => {
        if (typeof props?.setContextState === 'function') {
            props.setContextState(prevState => ({
                ...prevState,
                reprocess: {
                    ...prevState?.reprocess,
                    handleStartActionCallback: handleStartProcess,
                    handleStopActionCallback: handleStopProcess
                }
            }))
        }
    }, [selectedMethodology, selectedMethodology?.value])

    return (
        <>
            <MainContainer>
                <div className="title">
                    <span style={groupTitleStyle}>{i18n('Rig Floor Equipments')}</span>
                </div>
                <SectionRow className="section-row-top">
                    <EquipmentSelectSection
                        title="Hydratong"
                        titleClassName="kpi-generation-methodology"
                        tooltipId="kpi-generation-methodology-info-tooltip"
                        tooltipText="Here you will see the equipment associated with this RIG and a Hydratong archetype."
                        dataTestId="selector-realtime-kpi-generation-methodology"
                        linkColor={theme['link-color']}
                        disabled={isReprocessingData || !hasPermissionToEdit || !enableRigFloor || isRigFloorWaiting}
                        clearable={true}
                        options={hydratongEquipments}
                        value={selectedHydratongEquipment}
                        onChange={setSelectedHydratongEquipment}
                    />
                    <EquipmentSelectSection
                        title="Hydraracker"
                        titleClassName="rig-presets"
                        tooltipId="rig-presets-info-tooltip"
                        tooltipText="Here you will see the equipment associated with this RIG and a Hydraracker archetype."
                        dataTestId="selector-realtime-rig-presets"
                        linkColor={theme['link-color']}
                        linkClassName="equipment-link"
                        disabled={isReprocessingData || !hasPermissionToEdit || !enableRigFloor || isRigFloorWaiting}
                        clearable={true}
                        options={hydrarackerEquipments}
                        value={selectedHydrarackerEquipment}
                        onChange={setSelectedHydrarackerEquipment}
                    />
                    <EquipmentSelectSection
                        title="Top Drive"
                        titleClassName="rig-presets"
                        tooltipId="rig-presets-info-tooltip"
                        tooltipText="Here you will see the equipment associated with this RIG and a Top Drive archetype."
                        dataTestId="selector-realtime-rig-presets"
                        linkColor={theme['link-color']}
                        linkClassName="equipment-link"
                        disabled={isReprocessingData || !hasPermissionToEdit || !enableRigFloor || isRigFloorWaiting}
                        clearable={true}
                        options={topDriveEquipments}
                        value={selectedTopDriveEquipment}
                        onChange={setSelectedTopDriveEquipment}
                    />
                    {/* Rig Floor channels config */}
                    <Section>
                        <div className="section-name" />
                        {savedKpiOptions.isReprocessRunning ? (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip style={{ zIndex: 9999 }} id={'channels-configurations-btn-tooltip'}>
                                        {i18n(
                                            "It's not possible to configure channels while Reprocessing is Running. You need to stop Reprocessing to access Channels Configurations."
                                        )}
                                    </Tooltip>
                                }
                            >
                                <ChannelsConfigurationBtn
                                    id="kpi-options_realtime-channels-configuration"
                                    enabled={
                                        hasPermissionToEdit &&
                                        !isReprocessingData &&
                                        enableRigFloor &&
                                        !isRigFloorWaiting
                                    }
                                    onClick={() =>
                                        !isReprocessingData && !isRigFloorWaiting && setIsRigFloorChannelsOpened(true)
                                    }
                                >
                                    {i18n('Rig Floor channels config')}
                                </ChannelsConfigurationBtn>
                            </OverlayTrigger>
                        ) : (
                            <ChannelsConfigurationBtn
                                id="kpi-options_realtime-channels-configuration"
                                enabled={
                                    hasPermissionToEdit && enableRigFloor && !isReprocessingData && !isRigFloorWaiting
                                }
                                onClick={() => {
                                    if (
                                        hasPermissionToEdit &&
                                        enableRigFloor &&
                                        !isReprocessingData &&
                                        !isRigFloorWaiting
                                    ) {
                                        setIsRigFloorChannelsOpened(true)
                                    }
                                }}
                            >
                                {i18n('Rig Floor channels config')}
                            </ChannelsConfigurationBtn>
                        )}
                        <ChannelsInterpolationBadge active={isUsingRigFloorInterpolation} />
                    </Section>
                </SectionRow>
            </MainContainer>

            <Divisor />
            <MainContainer>
                <div className="title">
                    <span style={groupTitleStyle}>{i18n('Riser Equipments')}</span>
                </div>
                <SectionRow className="section-row-top">
                    <EquipmentSelectSection
                        title="RRT"
                        titleClassName="rig-presets"
                        tooltipId="rrt-presets-info-tooltip"
                        tooltipText="Here you will see the equipment associated with this RIG and a RRT archetype."
                        dataTestId="selector-reprocess-rrt"
                        linkColor={theme['link-color']}
                        disabled={isReprocessingData || !hasPermissionToEdit || !enableRigFloor || isRigFloorWaiting}
                        clearable={true}
                        options={rrtEquipments}
                        value={selectedRrtEquipment}
                        onChange={setSelectedRrtEquipment}
                    />
                    <EquipmentSelectSection
                        title="Catwalk"
                        titleClassName="rig-presets"
                        tooltipId="catwalk-presets-info-tooltip"
                        tooltipText="Here you will see the equipment associated with this RIG and a Catwalk archetype."
                        dataTestId="selector-reprocess-catwalk"
                        linkColor={theme['link-color']}
                        disabled={isReprocessingData || !hasPermissionToEdit || !enableRigFloor || isRigFloorWaiting}
                        clearable={true}
                        options={catwalkEquipments}
                        value={selectedCatwalkEquipment}
                        onChange={setSelectedCatwalkEquipment}
                    />
                    {/* Riser channels config */}
                    <FixedWidthConfigSection>
                        <div className="section-name" />
                        {savedKpiOptions.isReprocessRunning ? (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip
                                        style={{ zIndex: 9999 }}
                                        id={'riser-channels-configurations-btn-tooltip'}
                                    >
                                        {i18n(
                                            "It's not possible to configure channels while Reprocessing is Running. You need to stop Reprocessing to access Channels Configurations."
                                        )}
                                    </Tooltip>
                                }
                            >
                                <ChannelsConfigurationBtn
                                    id="kpi-options_realtime-riser-channels-configuration"
                                    enabled={
                                        hasPermissionToEdit &&
                                        !isReprocessingData &&
                                        enableRigFloor &&
                                        !isRigFloorWaiting
                                    }
                                    onClick={() =>
                                        !isReprocessingData && !isRigFloorWaiting && setIsRiserChannelsOpened(true)
                                    }
                                >
                                    {i18n('Riser channels config')}
                                </ChannelsConfigurationBtn>
                            </OverlayTrigger>
                        ) : (
                            <ChannelsConfigurationBtn
                                id="kpi-options_realtime-riser-channels-configuration"
                                enabled={
                                    hasPermissionToEdit && enableRigFloor && !isReprocessingData && !isRigFloorWaiting
                                }
                                onClick={() => {
                                    if (
                                        hasPermissionToEdit &&
                                        enableRigFloor &&
                                        !isReprocessingData &&
                                        !isRigFloorWaiting
                                    ) {
                                        setIsRiserChannelsOpened(true)
                                    }
                                }}
                            >
                                {i18n('Riser channels config')}
                            </ChannelsConfigurationBtn>
                        )}
                        <ChannelsInterpolationBadge active={isUsingRiserInterpolation} />
                    </FixedWidthConfigSection>
                </SectionRow>
            </MainContainer>

            {isRigFloorChannelsOpened && (
                <ChannelsConfigurationModal
                    title={i18n('Rig Floor channels configurations')}
                    isOpen={isRigFloorChannelsOpened}
                    setIsOpen={setIsRigFloorChannelsOpened}
                    setIsUsingInterpolation={setIsUsingRigFloorInterpolation}
                    externalSetInterpolationFrequency={setInterpolationFrequency}
                    externalSetMaxDeltaWithoutInterpolation={setMaxDeltaWithoutInterpolation}
                    currentConfig={{
                        useInterpolation: isUsingRigFloorInterpolation,
                        interpolationFrequency: String(interpolationFrequency),
                        maxDeltaWithoutInterpolation: String(maxDeltaWithoutInterpolation)
                    }}
                />
            )}
            {isRiserChannelsOpened && (
                <ChannelsConfigurationModal
                    title={i18n('Riser channels configurations')}
                    isOpen={isRiserChannelsOpened}
                    setIsOpen={setIsRiserChannelsOpened}
                    setIsUsingInterpolation={setIsUsingRiserInterpolation}
                    externalSetInterpolationFrequency={setRiserInterpolationFrequency}
                    externalSetMaxDeltaWithoutInterpolation={setRiserMaxDeltaWithoutInterpolation}
                    currentConfig={{
                        useInterpolation: isUsingRiserInterpolation,
                        interpolationFrequency: String(riserInterpolationFrequency),
                        maxDeltaWithoutInterpolation: String(riserMaxDeltaWithoutInterpolation)
                    }}
                />
            )}
        </>
    )
}

export default ReprocessRigFloorKpis
